export interface NameIdPair<T> {
    readonly id: T,
    readonly name: string,
}

export class Address {
    readonly address1: string = "";
    readonly address2?: string;
    readonly city: string = "";
    readonly state: string = "";
    readonly zipCode: string = "";
    readonly country: string = "";
}

export interface Contact {
    readonly id: number,
    readonly firstName?: string | null,
    readonly lastName?: string | null,
    readonly email?: string | null,
    readonly phone?: string | null,
    readonly fax?: string | null,
    readonly altPhone?: string | null,
    readonly altFax?: string | null,
    readonly isVerified: boolean,
    readonly electronicSetup?: string,
}

export interface User {
    readonly id: number,
    readonly firstName: string,
    readonly lastName: string,
    readonly displayName: string,
}

export interface DateRange {
    readonly from: Date,
    readonly to: Date,
}

export interface StartDateRange {
    readonly from: Date,
    readonly to?: Date,
}

export interface EndDateRange {
    readonly from?: Date,
    readonly to: Date,
}

export type PartialDateRange = StartDateRange | EndDateRange;

/**
 * A subdivision of a country. (U.S. state, Canadian province, etc.)
 *
 * This is not called "State" because there are classes all over called that
 * already. This name is also nation-neutral.
 */
export interface AdministrationLevel {
    readonly name: string,
    readonly abbreviation: string,
    readonly id: number,
}

export const UNITED_STATES: readonly AdministrationLevel[] = [
    {name: "Alaska", abbreviation: "AK", id: 1},
    {name: "Alabama", abbreviation: "AL", id: 2},
    {name: "Arkansas", abbreviation: "AR", id: 3},
    {name: "American Samoa", abbreviation: "AS", id: 4},
    {name: "Arizona", abbreviation: "AZ", id: 5},
    {name: "California", abbreviation: "CA", id: 6},
    {name: "Colorado", abbreviation: "CO", id: 7},
    {name: "Connecticut", abbreviation: "CT", id: 8},
    {name: "Washington DC", abbreviation: "DC", id: 9},
    {name: "Delaware", abbreviation: "DE", id: 10},
    {name: "Florida", abbreviation: "FL", id: 11},
    {name: "Georgia", abbreviation: "GA", id: 12},
    {name: "Guam", abbreviation: "GU", id: 13},
    {name: "Hawaii", abbreviation: "HI", id: 14},
    {name: "Iowa", abbreviation: "IA", id: 15},
    {name: "Idaho", abbreviation: "ID", id: 16},
    {name: "Illinois", abbreviation: "IL", id: 17},
    {name: "Indiana", abbreviation: "IN", id: 18},
    {name: "Kansas", abbreviation: "KS", id: 19},
    {name: "Kentucky", abbreviation: "KY", id: 20},
    {name: "Louisiana", abbreviation: "LA", id: 21},
    {name: "Massachusetts", abbreviation: "MA", id: 22},
    {name: "Maryland", abbreviation: "MD", id: 23},
    {name: "Maine", abbreviation: "ME", id: 24},
    {name: "Michigan", abbreviation: "MI", id: 25},
    {name: "Minnesota", abbreviation: "MN", id: 26},
    {name: "Missouri", abbreviation: "MO", id: 27},
    {name: "Mississippi", abbreviation: "MS", id: 28},
    {name: "Montana", abbreviation: "MT", id: 29},
    {name: "North Carolina", abbreviation: "NC", id: 30},
    {name: "North Dakota", abbreviation: "ND", id: 31},
    {name: "Nebraska", abbreviation: "NE", id: 32},
    {name: "New Hampshire", abbreviation: "NH", id: 33},
    {name: "New Jersey", abbreviation: "NJ", id: 34},
    {name: "New Mexico", abbreviation: "NM", id: 35},
    {name: "Nevada", abbreviation: "NV", id: 36},
    {name: "New York", abbreviation: "NY", id: 37},
    {name: "Ohio", abbreviation: "OH", id: 38},
    {name: "Oklahoma", abbreviation: "OK", id: 39},
    {name: "Oregon", abbreviation: "OR", id: 40},
    {name: "Pennsylvania", abbreviation: "PA", id: 41},
    {name: "Puerto Rico", abbreviation: "PR", id: 42},
    {name: "Rhode Island", abbreviation: "RI", id: 43},
    {name: "South Carolina", abbreviation: "SC", id: 44},
    {name: "South Dakota", abbreviation: "SD", id: 45},
    {name: "Tennessee", abbreviation: "TN", id: 46},
    {name: "Texas", abbreviation: "TX", id: 47},
    {name: "Utah", abbreviation: "UT", id: 48},
    {name: "Virginia", abbreviation: "VA", id: 49},
    {name: "Virgin Islands", abbreviation: "VI", id: 50},
    {name: "Vermont", abbreviation: "VT", id: 51},
    {name: "Washington", abbreviation: "WA", id: 52},
    {name: "Wisconsin", abbreviation: "WI", id: 53},
    {name: "West Virginia", abbreviation: "WV", id: 54},
    {name: "Wyoming", abbreviation: "WY", id: 55},
]
